/**
 * Page principale de gestion des permissions
 */

import React, { useState, useMemo } from 'react';
import { 
  PlusIcon, 
  CogIcon, 
  ShieldCheckIcon, 
  ChartBarIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { usePermissionManagement } from '@/hooks/usePermissionManagement';
import PermissionModal from '@/components/permissionManagement/PermissionModal';
import RoleModal from '@/components/permissionManagement/RoleModal';
import AssignPermissionModal from '@/components/permissionManagement/AssignPermissionModal';
import { Permission, Role, PermissionFormData, RoleFormData, UserRole, PermissionManagementStats } from '@/types/permissionManagement';

const CATEGORY_LABELS: Record<string, string> = {
  ACCESS: 'Accès',
  USER_MANAGEMENT: 'Gestion utilisateurs',
  SYSTEM_CONFIG: 'Configuration système',
  WEIGHING: 'Pesée',
  QUALITY_ANALYSIS: 'Analyse qualité',
  TICKET_GENERATION: 'Génération tickets',
  WEIGHING_CANCELLATION: 'Annulation pesée',
  REJECTION: 'Refoulement',
  STOCK_MANAGEMENT: 'Gestion stock',
  STATISTICS_REPORTS: 'Statistiques & rapports',
  EXPORT: 'Export',
  MULTI_SITE: 'Multi-sites',
  AUDIT: 'Audit',
};

const ACCESS_LEVEL_LABELS: Record<string, string> = {
  read: 'Lecture',
  write: 'Écriture',
  admin: 'Administration',
};

const StatProgressBar: React.FC<{ label: string; value: number; total: number; color: string }> = ({
  label,
  value,
  total,
  color,
}) => {
  const percent = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-700">{label}</span>
        <span className="text-gray-500">{value} ({percent}%)</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

const PermissionManagementPage: React.FC = () => {
  const {
    permissions,
    roles,
    loading,
    error,
    stats,
    refreshData,
    createPermission,
    updatePermission,
    deletePermission,
    createRole,
    updateRole,
    deleteRole,
    assignPermissionToRole,
    removePermissionFromRole,
    getRolePermissions,
    getRoleUsers
  } = usePermissionManagement();

  const [activeTab, setActiveTab] = useState<'permissions' | 'roles' | 'stats'>('permissions');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Pagination constants
  const ITEMS_PER_PAGE_OPTIONS = [5, 10, 20, 50];
  
  // Modals state
  const [permissionModal, setPermissionModal] = useState<{ isOpen: boolean; permission?: Permission }>({ isOpen: false });
  const [roleModal, setRoleModal] = useState<{ isOpen: boolean; role?: Role }>({ isOpen: false });
  const [assignModal, setAssignModal] = useState<{ isOpen: boolean; role?: Role }>({ isOpen: false });
  const [userAssignModal, setUserAssignModal] = useState<{ isOpen: boolean; role?: Role }>({ isOpen: false });
  const [assignedPermissions, setAssignedPermissions] = useState<Permission[]>([]);
  const [roleUsers, setRoleUsers] = useState<UserRole[]>([]);

  // Filtered and paginated data
  const filteredPermissions = useMemo(() => {
    return permissions.filter(permission => {
      const matchesSearch = (permission.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                           (permission.code?.toLowerCase() || '').includes(searchTerm.toLowerCase());
      const matchesCategory = !selectedCategory || permission.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [permissions, searchTerm, selectedCategory]);

  const paginatedPermissions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredPermissions.slice(startIndex, endIndex);
  }, [filteredPermissions, currentPage, itemsPerPage]);

  const filteredRoles = useMemo(() => {
    return roles.filter(role => {
      return (role.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
             (role.display_name?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    });
  }, [roles, searchTerm]);

  // Pagination info
  const totalPages = Math.ceil(filteredPermissions.length / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, filteredPermissions.length);

  // Categories
  const categories = Array.from(new Set(permissions.map(p => p.category))).sort();

  const displayStats = useMemo((): PermissionManagementStats => {
    const permissionsByCategory: Record<string, number> = {};
    const permissionsByAccessLevel: Record<string, number> = {};

    permissions.forEach((permission) => {
      if (!permission.is_active) return;
      permissionsByCategory[permission.category] = (permissionsByCategory[permission.category] || 0) + 1;
      const level = permission.access_level || 'read';
      permissionsByAccessLevel[level] = (permissionsByAccessLevel[level] || 0) + 1;
    });

    const activePermissions = permissions.filter((p) => p.is_active).length;
    const activeRoles = roles.filter((r) => r.is_active).length;
    const systemRoles = roles.filter((r) => r.is_system_role).length;
    const rolesSummary = [...roles]
      .sort((a, b) => (b.permissions_count || 0) - (a.permissions_count || 0))
      .map((role) => ({
        id: role.id,
        name: role.name,
        display_name: role.display_name || role.name,
        permissions_count: role.permissions_count || 0,
        users_count: role.users_count || 0,
        is_active: role.is_active,
        is_system_role: role.is_system_role,
      }));

    const totalRolePermissions = rolesSummary.reduce((sum, role) => sum + role.permissions_count, 0);

    const derived: PermissionManagementStats = {
      permissions: {
        total_permissions: permissions.length,
        active_permissions: activePermissions,
        inactive_permissions: permissions.length - activePermissions,
        permissions_by_category: permissionsByCategory,
        permissions_by_access_level: permissionsByAccessLevel,
        unassigned_permissions: Math.max(activePermissions - totalRolePermissions, 0),
      },
      roles: {
        total_roles: roles.length,
        active_roles: activeRoles,
        inactive_roles: roles.length - activeRoles,
        system_roles: systemRoles,
        custom_roles: roles.length - systemRoles,
        total_role_permissions: totalRolePermissions,
        roles_summary: rolesSummary,
      },
    };

    if (stats?.permissions && stats?.roles) {
      return {
        permissions: { ...derived.permissions, ...stats.permissions },
        roles: {
          ...derived.roles,
          ...stats.roles,
          roles_summary: stats.roles.roles_summary?.length
            ? stats.roles.roles_summary
            : derived.roles.roles_summary,
        },
      };
    }

    return derived;
  }, [stats, permissions, roles]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setCurrentPage(1); // Reset to first page when filtering by category
  };

  // Handlers
  const handleCreatePermission = async (data: PermissionFormData) => {
    await createPermission(data);
  };

  const handleUpdatePermission = async (data: PermissionFormData) => {
    if (permissionModal.permission) {
      await updatePermission(permissionModal.permission.id, data);
    }
  };

  const handleDeletePermission = async (permission: Permission) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer la permission "${permission.name}" ?`)) {
      await deletePermission(permission.id);
    }
  };

  const handleCreateRole = async (data: RoleFormData) => {
    await createRole(data);
  };

  const handleUpdateRole = async (data: RoleFormData) => {
    if (roleModal.role) {
      await updateRole(roleModal.role.id, data);
    }
  };

  const handleDeleteRole = async (role: Role) => {
    if (role.is_system_role) {
      alert('Les rôles système ne peuvent pas être supprimés');
      return;
    }
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le rôle "${role.display_name}" ?`)) {
      await deleteRole(role.id);
    }
  };

  const handleAssignPermission = async (data: any) => {
    await assignPermissionToRole(data);
    // Recharger les permissions assignées après assignation
    if (assignModal.role) {
      await loadRolePermissions(assignModal.role.id);
    }
  };

  const handleRemovePermission = async (data: any) => {
    await removePermissionFromRole(data);
    // Recharger les permissions assignées après suppression
    if (assignModal.role) {
      await loadRolePermissions(assignModal.role.id);
    }
  };

  const loadRolePermissions = async (roleId: number) => {
    try {
      const rolePermissions = await getRolePermissions(roleId);
      // S'assurer que nous avons un tableau
      const permissionsArray = Array.isArray(rolePermissions) ? rolePermissions : [];
      setAssignedPermissions(permissionsArray);
    } catch (error) {
      console.error('Erreur lors du chargement des permissions du rôle:', error);
      setAssignedPermissions([]);
    }
  };

  const handleOpenAssignModal = async (role: Role) => {
    setAssignModal({ isOpen: true, role });
    await loadRolePermissions(role.id);
  };

  const handleOpenUserAssignModal = async (role: Role) => {
    try {
      const users = await getRoleUsers(role.id);
      setRoleUsers(users);
      setUserAssignModal({ isOpen: true, role });
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs du rôle:', error);
      setRoleUsers([]);
      setUserAssignModal({ isOpen: true, role });
    }
  };

  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'read': return 'bg-blue-100 text-blue-800';
      case 'write': return 'bg-green-100 text-green-800';
      case 'admin': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getAccessLevelLabel = (level: string) => {
    switch (level) {
      case 'read': return 'Lecture';
      case 'write': return 'Écriture';
      case 'admin': return 'Admin';
      default: return level;
    }
  };

  if (loading && permissions.length === 0 && roles.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des données...</p>
            <p className="mt-2 text-sm text-gray-500">Récupération de toutes les permissions et rôles</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Erreur</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestion des Permissions</h1>
              <p className="mt-1 text-sm text-gray-600">
                Créez et gérez les permissions et rôles de la plateforme
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={refreshData}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </button>
              <button
                onClick={() => setPermissionModal({ isOpen: true })}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Nouvelle Permission
              </button>
              <button
                onClick={() => setRoleModal({ isOpen: true })}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Nouveau Rôle
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('permissions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'permissions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ShieldCheckIcon className="h-5 w-5 inline mr-2" />
              Permissions ({permissions.length})
              {loading && <span className="ml-2 text-xs text-blue-500">Chargement...</span>}
            </button>
            <button
              onClick={() => setActiveTab('roles')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'roles'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CogIcon className="h-5 w-5 inline mr-2" />
              Rôles ({roles.length})
              {loading && <span className="ml-2 text-xs text-blue-500">Chargement...</span>}
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stats'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ChartBarIcon className="h-5 w-5 inline mr-2" />
              Statistiques
            </button>
          </nav>
        </div>
      </div>

      {/* Search and Filters */}
      {activeTab !== 'stats' && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex space-x-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={`Rechercher ${activeTab === 'permissions' ? 'des permissions' : 'des rôles'}...`}
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            {activeTab === 'permissions' && (
              <div className="w-48">
                <select
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Toutes les catégories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="bg-white shadow rounded-lg">
        {activeTab === 'permissions' && (
          <div className="p-6">
            <div className="grid gap-4">
              {paginatedPermissions.map(permission => (
                <div key={permission.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-medium text-gray-900">
                          {permission.display_name || permission.name}
                        </h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${getAccessLevelColor(permission.access_level || 'read')}`}>
                          {getAccessLevelLabel(permission.access_level || 'read')}
                        </span>
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                          {permission.category}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-600 font-mono">{permission.code || permission.name}</p>
                      <p className="mt-2 text-sm text-gray-500">{permission.description}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setPermissionModal({ isOpen: true, permission })}
                        className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md"
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => handleDeletePermission(permission)}
                        className="px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {paginatedPermissions.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  {filteredPermissions.length === 0 ? (
                    <div>
                      <p>Aucune permission trouvée</p>
                      <p className="text-sm mt-2">
                        La base locale ne contient peut-être pas encore les permissions standard.
                        Utilisez le bouton « Nouvelle Permission » ou synchronisez via{' '}
                        <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">sync_permissions</code>.
                      </p>
                    </div>
                  ) : (
                    'Aucune permission sur cette page'
                  )}
                </div>
              )}
            </div>
            
            {/* Pagination Controls */}
            {filteredPermissions.length > 0 && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-6">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <label htmlFor="items-per-page" className="text-sm text-gray-700">
                      Afficher:
                    </label>
                    <select
                      id="items-per-page"
                      value={itemsPerPage}
                      onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {ITEMS_PER_PAGE_OPTIONS.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-700">par page</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    Affichage de {startItem} à {endItem} sur {filteredPermissions.length} résultat(s)
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-md border border-gray-300 text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  
                  <div className="flex space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-1 text-sm rounded-md ${
                            currentPage === pageNum
                              ? 'bg-orange-600 text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-md border border-gray-300 text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'roles' && (
          <div className="p-6">
            <div className="grid gap-4">
              {filteredRoles.map(role => (
                <div key={role.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-medium text-gray-900">{role.display_name}</h3>
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                          {role.name}
                        </span>
                        {role.is_system_role && (
                          <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                            Système
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-sm text-gray-500">{role.description}</p>
                      <div className="mt-2 flex space-x-4 text-sm text-gray-600">
                        <span>{role.permissions_count || 0} permission(s)</span>
                        <span>{role.users_count || 0} utilisateur(s)</span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleOpenAssignModal(role)}
                        className="px-3 py-1 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md"
                      >
                        Permissions
                      </button>
                      <button
                        onClick={() => handleOpenUserAssignModal(role)}
                        className="px-3 py-1 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-md"
                      >
                        Utilisateurs
                      </button>
                      <button
                        onClick={() => setRoleModal({ isOpen: true, role })}
                        className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md"
                      >
                        Modifier
                      </button>
                      {!role.is_system_role && (
                        <button
                          onClick={() => handleDeleteRole(role)}
                          className="px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md"
                        >
                          Supprimer
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {filteredRoles.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Aucun rôle trouvé
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="p-6 space-y-6">
            {/* Cartes récapitulatives */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <p className="text-sm text-blue-700">Permissions actives</p>
                <p className="text-3xl font-bold text-blue-900 mt-1">
                  {displayStats.permissions.active_permissions}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  sur {displayStats.permissions.total_permissions} au total
                </p>
              </div>
              <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                <p className="text-sm text-green-700">Rôles actifs</p>
                <p className="text-3xl font-bold text-green-900 mt-1">
                  {displayStats.roles.active_roles}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  sur {displayStats.roles.total_roles} au total
                </p>
              </div>
              <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
                <p className="text-sm text-purple-700">Associations rôle ↔ permission</p>
                <p className="text-3xl font-bold text-purple-900 mt-1">
                  {displayStats.roles.total_role_permissions ?? 0}
                </p>
                <p className="text-xs text-purple-600 mt-1">permissions assignées aux rôles</p>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                <p className="text-sm text-amber-700">Permissions non assignées</p>
                <p className="text-3xl font-bold text-amber-900 mt-1">
                  {displayStats.permissions.unassigned_permissions ?? 0}
                </p>
                <p className="text-xs text-amber-600 mt-1">sans rattachement à un rôle</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Permissions par catégorie */}
              <div className="border border-gray-200 rounded-lg p-5">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Permissions par catégorie</h3>
                <div className="space-y-3">
                  {Object.entries(displayStats.permissions.permissions_by_category)
                    .sort(([, a], [, b]) => b - a)
                    .map(([category, count]) => (
                      <StatProgressBar
                        key={category}
                        label={CATEGORY_LABELS[category] || category}
                        value={count}
                        total={displayStats.permissions.active_permissions || 1}
                        color="bg-blue-500"
                      />
                    ))}
                  {Object.keys(displayStats.permissions.permissions_by_category).length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">Aucune permission active</p>
                  )}
                </div>
              </div>

              {/* Permissions par niveau d'accès */}
              <div className="border border-gray-200 rounded-lg p-5">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Permissions par niveau d'accès</h3>
                <div className="space-y-3">
                  {Object.entries(displayStats.permissions.permissions_by_access_level || {})
                    .sort(([, a], [, b]) => b - a)
                    .map(([level, count]) => (
                      <StatProgressBar
                        key={level}
                        label={ACCESS_LEVEL_LABELS[level] || level}
                        value={count}
                        total={displayStats.permissions.active_permissions || 1}
                        color={
                          level === 'admin' ? 'bg-red-500' : level === 'write' ? 'bg-green-500' : 'bg-blue-400'
                        }
                      />
                    ))}
                  {Object.keys(displayStats.permissions.permissions_by_access_level || {}).length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">Aucune donnée disponible</p>
                  )}
                </div>
              </div>
            </div>

            {/* Détail rôles */}
            <div className="border border-gray-200 rounded-lg p-5">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Répartition par rôle</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rôle</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Permissions</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Utilisateurs</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(displayStats.roles.roles_summary || []).map((role) => (
                      <tr key={role.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{role.display_name}</div>
                          <div className="text-xs text-gray-500 font-mono">{role.name}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                              role.is_system_role
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {role.is_system_role ? 'Système' : 'Personnalisé'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-sm font-medium text-gray-900">
                          {role.permissions_count}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-700">{role.users_count}</td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                              role.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {role.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(displayStats.roles.roles_summary || []).length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-6">Aucun rôle disponible</p>
                )}
              </div>
            </div>

            {/* Synthèse rôles */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-blue-900 mb-4">Détail permissions</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-700">Total</span>
                    <span className="font-medium text-blue-900">{displayStats.permissions.total_permissions}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-700">Actives</span>
                    <span className="font-medium text-blue-900">{displayStats.permissions.active_permissions}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-700">Inactives</span>
                    <span className="font-medium text-blue-900">{displayStats.permissions.inactive_permissions}</span>
                  </div>
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-green-900 mb-4">Détail rôles</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-green-700">Total</span>
                    <span className="font-medium text-green-900">{displayStats.roles.total_roles}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-green-700">Système</span>
                    <span className="font-medium text-green-900">{displayStats.roles.system_roles}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-green-700">Personnalisés</span>
                    <span className="font-medium text-green-900">{displayStats.roles.custom_roles}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-green-700">Inactifs</span>
                    <span className="font-medium text-green-900">{displayStats.roles.inactive_roles}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <PermissionModal
        isOpen={permissionModal.isOpen}
        onClose={() => setPermissionModal({ isOpen: false })}
        permission={permissionModal.permission}
        onSave={permissionModal.permission ? handleUpdatePermission : handleCreatePermission}
      />

      <RoleModal
        isOpen={roleModal.isOpen}
        onClose={() => setRoleModal({ isOpen: false })}
        role={roleModal.role}
        onSave={roleModal.role ? handleUpdateRole : handleCreateRole}
      />

      {assignModal.role && (
        <AssignPermissionModal
          isOpen={assignModal.isOpen}
          onClose={() => setAssignModal({ isOpen: false })}
          role={assignModal.role}
          availablePermissions={permissions}
          assignedPermissions={assignedPermissions}
          onAssign={handleAssignPermission}
          onRemove={handleRemovePermission}
        />
      )}

      {/* Modal temporaire pour l'assignation des utilisateurs */}
      {userAssignModal.role && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Gérer les utilisateurs du rôle "{userAssignModal.role.display_name}"
              </h2>
              <button
                onClick={() => setUserAssignModal({ isOpen: false })}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  {roleUsers.length} utilisateur(s) assigné(s) à ce rôle
                </p>
              </div>
              
              {roleUsers.length > 0 ? (
                <div className="space-y-3">
                  {roleUsers.map((userRole) => (
                    <div key={userRole.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 bg-blue-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-medium text-sm">
                                {userRole.user_full_name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {userRole.user_full_name}
                            </p>
                            <p className="text-sm text-gray-500 truncate">
                              {userRole.user_username} • {userRole.user_email}
                            </p>
                            <p className="text-xs text-gray-400">
                              Assigné le {new Date(userRole.assigned_at).toLocaleDateString('fr-FR')}
                              {userRole.assigned_by_username && ` par ${userRole.assigned_by_username}`}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          userRole.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {userRole.is_active ? 'Actif' : 'Inactif'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>Aucun utilisateur assigné à ce rôle</p>
                  <p className="text-sm mt-2">Utilisez la gestion des utilisateurs pour assigner des rôles</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionManagementPage;